/**
 * ChatContentV2 - Main chat content container (Mantine)
 *
 * Combines ChatHeaderV2, ChatBody, and ChatInputV2 into a complete chat UI.
 * Uses the shared chat store for state management.
 * Can be used in drawer mode or embedded mode.
 */

import { useRef, useLayoutEffect, useCallback, useMemo } from 'react';
import {
  Stack,
  Divider,
  ScrollArea,
  Loader,
  Text,
  Alert,
  Box,
  Center,
} from '@mantine/core';
import { MdErrorOutline } from 'react-icons/md';
import { useMutation } from '@tanstack/react-query';
import {
  useChatStore,
  combineAndSortMessages,
  useChatActiveChat,
  useChatMessages,
  useChatSystemMessages,
  useChatMembers,
  useChatIsLoading,
  useChatIsSending,
  useChatError,
  useChatIsSendingAllowed,
  useChatInitialFirstUnreadMessageId,
  useChatHasMoreOlder,
  useChatIsLoadingOlder,
} from '@stores/chatStore';
import ChatBody from '@components/Chat/ChatBody/ChatBody';
import { ChatType } from '@th-types/chat.type';
import { isAnnouncementChat } from '@constants/directChat';
import ChatHeaderV2 from './ChatHeaderV2';
import ChatInputV2 from './ChatInputV2';

const DM_POLICY_NOTICE = (
  <>
    Direct messaging is ONLY for specific job opportunities, not general
    database building. Companies found in violation could lose this
    functionality. If any messages go against this, please email{' '}
    <a href="mailto:support@trustedherd.com">support@trustedherd.com</a> -
    thanks!
  </>
);

const LOAD_OLDER_SCROLL_THRESHOLD_PX = 80;

interface ChatContentV2Props {
  /** Whether this is embedded mode (no header close button behavior change) */
  embedded?: boolean;
  /** Callback when VIEW PROFILE is clicked */
  onViewProfile?: () => void;
  /** Custom height (defaults to 100% of parent) */
  height?: string | number;
}

function ChatContentV2({
  embedded = false,
  onViewProfile,
  height = '100%',
}: ChatContentV2Props) {
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const stickToBottomRef = useRef(true);
  const pendingScrollRestoreRef = useRef<number | null>(null);
  const initialScrollDoneRef = useRef(false);
  const lastChatIdRef = useRef<number | null>(null);
  const unreadSeekCountRef = useRef<number | null>(null);

  // Get state from store
  const activeChat = useChatActiveChat();
  const messages = useChatMessages();
  const systemMessages = useChatSystemMessages();
  const chatMembers = useChatMembers();
  const isLoading = useChatIsLoading();
  const isSending = useChatIsSending();
  const error = useChatError();
  const isSendingAllowed = useChatIsSendingAllowed();
  const firstUnreadMessageId = useChatInitialFirstUnreadMessageId();
  const hasMoreOlder = useChatHasMoreOlder();
  const isLoadingOlder = useChatIsLoadingOlder();

  // Get actions from store
  const closeChat = useChatStore((state) => state.closeChat);
  const sendMessage = useChatStore((state) => state.sendMessage);
  const removeMessage = useChatStore((state) => state.removeMessage);
  const clearError = useChatStore((state) => state.clearError);
  const loadOlderMessages = useChatStore((state) => state.loadOlderMessages);

  // Combine and sort messages
  const combinedMessages = useMemo(
    () => combineAndSortMessages(messages, systemMessages),
    [messages, systemMessages]
  );

  const chatId = activeChat?.jobAssignmentId ?? null;

  /**
   * Capture the scroll height before requesting the page: the top loader mounts
   * as soon as the fetch starts, so measuring afterwards would leave the
   * restore below off by the loader's height.
   */
  const requestOlderMessages = useCallback(() => {
    const viewport = viewportRef.current;
    if (!viewport || !hasMoreOlder || isLoadingOlder || isLoading) return;

    pendingScrollRestoreRef.current = viewport.scrollHeight;
    stickToBottomRef.current = false;
    loadOlderMessages();
  }, [hasMoreOlder, isLoadingOlder, isLoading, loadOlderMessages]);

  // Stick to bottom on initial open / new outgoing sends; restore offset when prepending older;
  // on first open with unread, scroll to the unread divider.
  useLayoutEffect(() => {
    if (chatId !== lastChatIdRef.current) {
      lastChatIdRef.current = chatId;
      initialScrollDoneRef.current = false;
      stickToBottomRef.current = true;
      unreadSeekCountRef.current = null;
    }

    const viewport = viewportRef.current;
    if (!viewport || isLoading || combinedMessages.length === 0) return;

    const focusInput = () => {
      if (inputRef.current && !isSending) {
        inputRef.current.focus();
        inputRef.current.setSelectionRange(
          inputRef.current.value.length,
          inputRef.current.value.length
        );
      }
    };

    if (!initialScrollDoneRef.current) {
      if (firstUnreadMessageId != null) {
        const divider = viewport.querySelector('[data-unread-divider]');
        if (divider) {
          initialScrollDoneRef.current = true;
          pendingScrollRestoreRef.current = null;
          divider.scrollIntoView({ block: 'start' });
          requestAnimationFrame(focusInput);
          return;
        }
        if (
          combinedMessages.some(
            (message) => message.id === firstUnreadMessageId
          )
        ) {
          return;
        }
        if (hasMoreOlder) {
          pendingScrollRestoreRef.current = null;
          if (isLoadingOlder) return;
          if (unreadSeekCountRef.current !== combinedMessages.length) {
            unreadSeekCountRef.current = combinedMessages.length;
            loadOlderMessages();
            return;
          }
        }
      }
      initialScrollDoneRef.current = true;
    }

    if (pendingScrollRestoreRef.current != null) {
      const previousHeight = pendingScrollRestoreRef.current;
      viewport.scrollTop = viewport.scrollHeight - previousHeight;
      pendingScrollRestoreRef.current = null;
      return;
    }

    if (stickToBottomRef.current) {
      viewport.scrollTop = viewport.scrollHeight;
      requestAnimationFrame(focusInput);
    }
  }, [
    chatId,
    combinedMessages,
    isLoading,
    isSending,
    firstUnreadMessageId,
    hasMoreOlder,
    isLoadingOlder,
    loadOlderMessages,
  ]);

  const handleScrollPositionChange = useCallback(
    ({ y }: { x: number; y: number }) => {
      const viewport = viewportRef.current;
      if (!viewport) return;

      const distanceFromBottom =
        viewport.scrollHeight - y - viewport.clientHeight;
      stickToBottomRef.current = distanceFromBottom < 40;

      if (y <= LOAD_OLDER_SCROLL_THRESHOLD_PX) {
        requestOlderMessages();
      }
    },
    [requestOlderMessages]
  );

  // Handle message submission
  const handleSubmit = useCallback(
    async (text: string) => {
      stickToBottomRef.current = true;
      await sendMessage(text);
    },
    [sendMessage]
  );

  // Handle close
  const handleClose = useCallback(() => {
    if (!embedded) {
      closeChat();
    }
  }, [embedded, closeChat]);

  // Resend mutation (for ChatBody compatibility)
  // Removes the failed message first, then sendMessage creates a fresh optimistic message
  const resendMessageMutation = useMutation({
    mutationFn: async ({
      message,
      messageId,
    }: {
      message: string;
      messageId: number;
    }) => {
      removeMessage(messageId);
      stickToBottomRef.current = true;
      const success = await sendMessage(message);
      return { success };
    },
  });

  // Don't render if no active chat
  if (!activeChat) {
    return (
      <Stack align="center" justify="center" h={height} c="dimmed">
        <Text>No chat selected</Text>
      </Stack>
    );
  }

  const isAnnouncement = isAnnouncementChat(activeChat);
  const canViewProfile = !isAnnouncement && !!onViewProfile;

  return (
    <Stack h={height} gap={0}>
      {/* Header */}
      <ChatHeaderV2
        entity={activeChat.entity}
        jobInfo={activeChat.jobInfo}
        showContactInfo={activeChat.showContactInfo}
        isDirectMessage={activeChat.chatType === ChatType.DIRECT}
        isAnnouncement={isAnnouncement}
        statusBadges={activeChat.statusBadges}
        onViewProfile={canViewProfile ? onViewProfile : undefined}
        onClose={handleClose}
      />

      <Divider />

      {activeChat.chatType === ChatType.DIRECT && !isAnnouncement && (
        <>
          <Box
            px="md"
            py="xs"
            bg="color-mix(in srgb, var(--light-yellow) 50%, white)"
          >
            <Text size="xs" fs="italic">
              {DM_POLICY_NOTICE}
            </Text>
          </Box>
          <Divider />
        </>
      )}

      {/* Messages Area */}
      <ScrollArea
        viewportRef={viewportRef}
        flex={1}
        style={{ minHeight: 0 }}
        type="scroll"
        px="md"
        onScrollPositionChange={handleScrollPositionChange}
      >
        {/* Loading State */}
        {isLoading && (
          <Stack align="center" justify="center" h={200}>
            <Loader size="lg" />
            <Text c="dimmed">Loading messages...</Text>
          </Stack>
        )}

        {/* Error State */}
        {error && !isLoading && (
          <Alert
            icon={<MdErrorOutline size={16} />}
            title="Error"
            color="red"
            my="md"
            withCloseButton
            onClose={clearError}
          >
            {error}
          </Alert>
        )}

        {/* Empty State */}
        {!isLoading && !error && combinedMessages.length === 0 && (
          <Stack align="center" justify="center" h={200} c="dimmed">
            <Text>No messages yet</Text>
            <Text size="sm">Start the conversation!</Text>
          </Stack>
        )}

        {/* Messages — kept visible even when `error` is set, so a failed
            older-page or send request does not blank the conversation. */}
        {!isLoading && combinedMessages.length > 0 && (
          <>
            {isLoadingOlder && (
              <Center py="sm" data-testid="load-older-messages-loader">
                <Loader size="sm" />
              </Center>
            )}
            <ChatBody
              chatMessages={combinedMessages}
              chatMembers={chatMembers}
              resendMessageMutation={resendMessageMutation}
              firstUnreadMessageId={firstUnreadMessageId}
            />
          </>
        )}
      </ScrollArea>

      {!isAnnouncement && (
        <>
          <Divider />

          {/* Input Area */}
          <ChatInputV2
            ref={inputRef}
            onSubmit={handleSubmit}
            disabled={!isSendingAllowed}
            isSending={isSending}
            placeholder={
              !isSendingAllowed
                ? 'Messaging is not available for this job'
                : undefined
            }
          />
        </>
      )}
    </Stack>
  );
}

export default ChatContentV2;
